import React, { useState, useCallback } from 'react';
import { Card, BlockStack, Text, InlineStack, Button, Modal, Badge } from '@shopify/polaris';
import { QuestionCircleIcon } from '@shopify/polaris-icons';
import { HISTOGRAM_DEFAULTS, SCORE_TIERS, CHART_COLORS, BRAND_COLORS, type ScoreTier } from '../theme';

/**
 * T082: DistributionHistogram component.
 * Bar chart of tier-based product counts (AI-Discoverable/AI-Readable/AI-Confusing/AI-Invisible).
 * Uses inline SVG and theme config for colours and dimensions.
 *
 * @see documents/ui-enhancement-spec.md §4.1 (Score Tiers), §13
 */

interface TierDistribution {
  excellent: number;
  good: number;
  warning: number;
  critical: number;
}

interface DistributionHistogramProps {
  distribution: TierDistribution;
  title?: string;
}

const CHART_WIDTH = HISTOGRAM_DEFAULTS.width;
const CHART_HEIGHT = HISTOGRAM_DEFAULTS.height;
const PADDING = HISTOGRAM_DEFAULTS.padding;
const BAR_GAP = HISTOGRAM_DEFAULTS.barGap;

const TIER_ORDER: ScoreTier[] = ['excellent', 'good', 'warning', 'critical'];

export function DistributionHistogram({
  distribution,
  title = 'Score Distribution',
}: DistributionHistogramProps): React.ReactElement {
  const [helpOpen, setHelpOpen] = useState(false);
  const toggleHelp = useCallback(() => setHelpOpen((o) => !o), []);

  const values = TIER_ORDER.map((t) => distribution[t] ?? 0);
  const maxValue = Math.max(...values, 1);
  const total = values.reduce((sum, v) => sum + v, 0);

  const innerWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const innerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const barWidth = (innerWidth - BAR_GAP * (TIER_ORDER.length - 1)) / TIER_ORDER.length;

  if (total === 0) {
    return (
      <Card>
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">{title}</Text>
            <Button variant="plain" icon={QuestionCircleIcon} onClick={toggleHelp} accessibilityLabel="Score tier explanation" />
          </InlineStack>
          <Text as="p" variant="bodySm" tone="subdued">
            No scored products yet. Run a scan to see the score distribution.
          </Text>
          {helpOpen && <ScoreTierHelpModal open={helpOpen} onClose={toggleHelp} />}
        </BlockStack>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">{title}</Text>
          <InlineStack gap="200" blockAlign="center">
            <Text as="span" variant="bodySm" tone="subdued">{total} products</Text>
            <Button variant="plain" icon={QuestionCircleIcon} onClick={toggleHelp} accessibilityLabel="Score tier explanation" />
          </InlineStack>
        </InlineStack>

        <svg
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          width="100%"
          role="img"
          aria-label={`Score distribution: AI-Discoverable=${distribution.excellent}, AI-Readable=${distribution.good}, AI-Confusing=${distribution.warning}, AI-Invisible=${distribution.critical}`}
        >
          {/* Y-axis grid lines */}
          {[0.25, 0.5, 0.75, 1].map((fraction, i) => {
            const y = PADDING.top + innerHeight * (1 - fraction);
            return (
              <g key={`grid-${i}`}>
                <line
                  x1={PADDING.left}
                  y1={y}
                  x2={CHART_WIDTH - PADDING.right}
                  y2={y}
                  stroke={`var(--z-chart-grid, ${CHART_COLORS.gridLight})`}
                  strokeWidth="1"
                />
                <text
                  x={PADDING.left - 8}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill={`var(--z-chart-label, ${CHART_COLORS.labelLight})`}
                >
                  {Math.round(maxValue * fraction)}
                </text>
              </g>
            );
          })}

          {/* Bars */}
          {TIER_ORDER.map((tier, i) => {
            const value = distribution[tier];
            const barHeight = (value / maxValue) * innerHeight;
            const x = PADDING.left + i * (barWidth + BAR_GAP);
            const y = PADDING.top + innerHeight - barHeight;
            const tierConfig = SCORE_TIERS[tier];

            return (
              <g key={tier}>
                {/* Bar */}
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill={tierConfig.cssVar}
                  rx="3"
                  ry="3"
                />
                {/* Value on top */}
                {value > 0 && (
                  <text
                    x={x + barWidth / 2}
                    y={y - 5}
                    textAnchor="middle"
                    fontSize="12"
                    fontWeight="bold"
                    fill={tierConfig.cssVar}
                  >
                    {value}
                  </text>
                )}
                {/* Tier label */}
                <text
                  x={x + barWidth / 2}
                  y={CHART_HEIGHT - 10}
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight="normal"
                  fill={`var(--z-primary, ${BRAND_COLORS.primaryDark})`}
                >
                  {tierConfig.label}
                </text>
              </g>
            );
          })}
        </svg>
        <ScoreTierHelpModal open={helpOpen} onClose={toggleHelp} />
      </BlockStack>
    </Card>
  );
}

const SCORE_TIER_HELP: Array<{
  tier: ScoreTier;
  range: string;
  desc: string;
}> = [
  {
    tier: 'excellent',
    range: '90–100',
    desc: 'Top tier. Product has a valid GTIN/barcode, unique SKU, MPN, rich description, alt-text on all images, and full structured data. AI assistants will confidently surface it in shopping and comparison queries.',
  },
  {
    tier: 'good',
    range: '70–89',
    desc: 'Good enough for most queries, but missing one or two signals — typically a barcode, MPN, or alt-text. AI can understand the product but may rank it below Discoverable competitors. Worth fixing to close the gap.',
  },
  {
    tier: 'warning',
    range: '40–69',
    desc: 'Notable gaps in data quality. AI may partially understand the product but will frequently misrepresent it or skip it in favour of better-structured alternatives.',
  },
  {
    tier: 'critical',
    range: '0–39',
    desc: 'Critical data missing — likely no description, no identifiers, or very poor content. AI assistants cannot reliably understand or recommend this product.',
  },
];

function ScoreTierHelpModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): React.ReactElement {
  return (
    <Modal open={open} onClose={onClose} title="Score Tiers Explained">
      <Modal.Section>
        <BlockStack gap="400">
          {SCORE_TIER_HELP.map(({ tier, range, desc }) => (
            <BlockStack key={tier} gap="100">
              <InlineStack gap="200" blockAlign="center">
                <Badge tone={SCORE_TIERS[tier].badgeTone}>{SCORE_TIERS[tier].label}</Badge>
                <Text as="span" variant="bodySm" tone="subdued">{range}</Text>
              </InlineStack>
              <Text as="p" variant="bodySm">{desc}</Text>
            </BlockStack>
          ))}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
